// src/pages/sales-order/components/FieldRenderer.jsx
import React from "react";

/** handles input/select/multiselect/textarea/file/checkbox generically */
export default function FieldRenderer({
    field,
    value,
    onChange,
    error,
    readOnly = false,
    idPrefix = "fld",
    getInputTypeForField,
    getValueForField,
}) {
    const fid = `${idPrefix}-${field.name}`;
    const effectiveReadOnly = !!field.readOnly || readOnly;

    const common = {
        id: fid,
        name: field.name,
        disabled: field.type === "select" || field.type === "multiselect" ? effectiveReadOnly : undefined,
        readOnly: field.type !== "select" && field.type !== "multiselect" ? effectiveReadOnly : undefined,
        className: `form-control ${effectiveReadOnly ? "bg-light" : ""} ${error ? "is-invalid" : ""}`,
        style: effectiveReadOnly ? { pointerEvents: "none", backgroundColor: "#f8f9fa" } : {},
        onChange,
    };

    const renderLabel = () => (
        <label className="form-label" htmlFor={fid}>
            {field.label} {field.required && <span className="text-danger">*</span>}
        </label>
    );

    if (field.type === "select") {
        return (
            <div>
                {renderLabel()}
                <select
                    id={fid}
                    name={field.name}
                    className={`form-select ${effectiveReadOnly ? "bg-light" : ""} ${error ? "is-invalid" : ""}`}
                    value={value ?? ""}
                    onChange={onChange}
                    disabled={effectiveReadOnly}
                    style={effectiveReadOnly ? { pointerEvents: "none" } : {}}
                >
                    {Array.isArray(field.options) && field.options.length > 0 ? (
                        <>
                            {!field.required && <option value="">Select</option>}
                            {field.options.map((opt) =>
                                typeof opt === "object" ? (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ) : (
                                    <option key={opt} value={opt}>{opt}</option>
                                )
                            )}
                        </>
                    ) : (
                        <option value="">{field.required ? "Select" : ""}</option>
                    )}
                </select>
                {error && <div className="invalid-feedback d-block">{error}</div>}
            </div>
        );
    }

    if (field.type === "multiselect") {
        return (
            <div>
                {renderLabel()}
                <select
                    id={fid}
                    name={field.name}
                    multiple
                    className={`form-select ${effectiveReadOnly ? "bg-light" : ""} ${error ? "is-invalid" : ""}`}
                    value={Array.isArray(value) ? value : []}
                    onChange={(e) =>
                        onChange({
                            target: {
                                name: field.name,
                                value: Array.from(e.target.selectedOptions, (o) => o.value),
                            },
                        })
                    }
                    disabled={effectiveReadOnly}
                    style={effectiveReadOnly ? { pointerEvents: "none" } : {}}
                >
                    {(field.options || []).map((opt) =>
                        typeof opt === "object" ? (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ) : (
                            <option key={opt} value={opt}>{opt}</option>
                        )
                    )}
                </select>
                {error && <div className="invalid-feedback d-block">{error}</div>}
            </div>
        );
    }

    if (field.type === "textarea") {
        return (
            <div>
                {renderLabel()}
                <textarea {...common} rows={3} value={value ?? ""} />
                {error && <div className="invalid-feedback d-block">{error}</div>}
            </div>
        );
    }

    if (field.type === "file") {
        return (
            <div>
                {renderLabel()}
                <input
                    id={fid}
                    type="file"
                    className={`form-control ${effectiveReadOnly ? "bg-light" : ""} ${error ? "is-invalid" : ""}`}
                    name={field.name}
                    onChange={onChange}
                    disabled={effectiveReadOnly}
                />
                {error && <div className="invalid-feedback d-block">{error}</div>}
            </div>
        );
    }

    if (field.type === "checkbox") {
        return (
            <div className="form-check mt-2">
                <input
                    type="checkbox"
                    className="form-check-input"
                    id={fid}
                    name={field.name}
                    checked={!!value}
                    onChange={onChange}
                    disabled={effectiveReadOnly}
                />
                <label className="form-check-label" htmlFor={fid}>
                    {field.label}
                </label>
                {error && <div className="invalid-feedback d-block">{error}</div>}
            </div>
        );
    }

    // input
    return (
        <div>
            {renderLabel()}
            <input
                id={fid}
                type={getInputTypeForField ? getInputTypeForField(field) : field.type || "text"}
                {...common}
                value={getValueForField ? getValueForField(field, value) : value ?? ""}
            />
            {error && <div className="invalid-feedback d-block">{error}</div>}
        </div>
    );
}
